'use client';

import type {
  AnalyticsAdapter,
  UserProps,
  PageViewProps,
  ExportProps,
  ChangeDateFilterProps,
  SortTableColumnsProps,
  PinTableColumnProps,
  ChangeTablePageSizeProps,
  SearchTableContentsProps,
  UpdatePropertySelectionProps,
  SearchInPropertySelectorProps,
  DownloadInvoiceProps,
  ChangeResolutionProps,
} from './types';

/**
 * Central analytics tracker that composes multiple adapters.
 * Calls all adapters in sequence for each tracking call.
 *
 * To remove an adapter: delete it from the array passed to createAnalyticsTracker.
 * To add a new platform: implement AnalyticsAdapter and add it here.
 */
export interface CompositeTracker extends AnalyticsAdapter {
  addAdapter(adapter: AnalyticsAdapter): void;
}

/**
 * Factory function that creates a composite tracker.
 */
export const createAnalyticsTracker = (adapters: AnalyticsAdapter[] = []): CompositeTracker => {
  const adapterList = [...adapters];

  return {
    addAdapter(adapter: AnalyticsAdapter): void {
      adapterList.push(adapter);
    },

    setUser(user: UserProps): void {
      adapterList.forEach((adapter) => adapter.setUser(user));
    },

    resetUser(): void {
      adapterList.forEach((adapter) => adapter.resetUser());
    },

    trackLogin(): void {
      adapterList.forEach((adapter) => adapter.trackLogin());
    },

    trackPageView(props: PageViewProps): void {
      adapterList.forEach((adapter) => adapter.trackPageView(props));
    },

    trackExport(props: ExportProps): void {
      adapterList.forEach((adapter) => adapter.trackExport(props));
    },

    trackChangeDateFilter(props: ChangeDateFilterProps): void {
      adapterList.forEach((adapter) => adapter.trackChangeDateFilter(props));
    },

    trackSortTableColumns(props: SortTableColumnsProps): void {
      adapterList.forEach((adapter) => adapter.trackSortTableColumns(props));
    },

    trackPinTableColumn(props: PinTableColumnProps): void {
      adapterList.forEach((adapter) => adapter.trackPinTableColumn(props));
    },

    trackChangeTablePageSize(props: ChangeTablePageSizeProps): void {
      adapterList.forEach((adapter) => adapter.trackChangeTablePageSize(props));
    },

    trackSearchTableContents(props: SearchTableContentsProps): void {
      adapterList.forEach((adapter) => adapter.trackSearchTableContents(props));
    },

    trackUpdatePropertySelection(props: UpdatePropertySelectionProps): void {
      adapterList.forEach((adapter) => adapter.trackUpdatePropertySelection(props));
    },

    trackSearchInPropertySelector(props: SearchInPropertySelectorProps): void {
      adapterList.forEach((adapter) => adapter.trackSearchInPropertySelector(props));
    },

    trackDownloadInvoice(props: DownloadInvoiceProps): void {
      adapterList.forEach((adapter) => adapter.trackDownloadInvoice(props));
    },

    trackChangeResolution(props: ChangeResolutionProps): void {
      adapterList.forEach((adapter) => adapter.trackChangeResolution(props));
    },
  };
};
